/**
 * Fila projetada (decisão 1 do Igor): os abertos consomem a capacidade a
 * partir de hoje, no ritmo medido, na ordem das entregas. O período que a
 * fila não chega a encher é onde cabe venda.
 *
 * O volume da fila é confiável (erro medido de ~10%); a ORDEM de saída não
 * (a fábrica adianta clientes). Por isso a fronteira sai como faixa e a
 * posição de cada cliente é indicativa.
 */
import { contarDiasUteis, diaUtilDeOrdem, maiorIso, somarDiasCorridos, somarDu } from "./calendario";
import { CAMADA, CAMADAS_DA_FILA } from "./classificacao";
import { PRECISAO_REFERENCIA } from "./precisao";

export const LIMITE_APERTADO = 80;
export const LIMITE_FILA_CHEIA = 95;

export const FAIXA = Object.freeze({
  CABE: Object.freeze({ codigo: "cabe", tom: "success", rotulo: "Cabe" }),
  APERTADO: Object.freeze({ codigo: "apertado", tom: "warning", rotulo: "Apertado" }),
  FILA_CHEIA: Object.freeze({ codigo: "fila_cheia", tom: "danger", rotulo: "Fila cheia" }),
});

// k / ritmo em ponto flutuante: 4 / 2 pode dar 2,0000000001 e empurrar o
// item para o dia seguinte.
const EPSILON = 1e-9;

function compararTexto(a, b) {
  return String(a || "").localeCompare(String(b || ""), "pt-BR", { numeric: true });
}

/** Pedido numérico quando os dois são números; senão texto com números. */
function compararPedido(a, b) {
  const na = Number(a);
  const nb = Number(b);
  if (a && b && Number.isFinite(na) && Number.isFinite(nb)) return na - nb;
  return compararTexto(a, b);
}

// Data nula vai para o fim.
function compararData(a, b) {
  if (a === b) return 0;
  if (!a) return 1;
  if (!b) return -1;
  return a < b ? -1 : 1;
}

function desempate(a, b) {
  return compararPedido(a.pedido, b.pedido) || compararTexto(a.ambiente, b.ambiente);
}

/**
 * Camadas que a projeção consome: as da planilha e as que vêm de fora dela
 * (venda assinada aguardando o PCP e venda simulada). As de fora não entram
 * em `CAMADAS_DA_FILA`: o Cenário do PCP e as exceções só falam da planilha.
 */
export const CAMADAS_PROJETADAS = Object.freeze([...CAMADAS_DA_FILA, CAMADA.VENDA_ASSINADA, CAMADA.VENDA_SIMULADA]);

/**
 * Prioridade de origem na mesma data de entrega: a planilha, depois a venda
 * assinada (já é compromisso com o cliente, só falta digitar) e por último
 * a simulada (ainda nem foi vendida).
 */
const ORDEM_DA_ORIGEM = Object.freeze({ [CAMADA.VENDA_ASSINADA]: 1, [CAMADA.VENDA_SIMULADA]: 2 });

/** O item está na planilha (não é venda assinada nem simulada)? */
export function ehDaPlanilha(item) {
  return !ORDEM_DA_ORIGEM[item?.camada];
}

function compararOrigem(a, b) {
  return (ORDEM_DA_ORIGEM[a.camada] || 0) - (ORDEM_DA_ORIGEM[b.camada] || 0);
}

/**
 * Ordem da fila: em produção (por início, depois entrega) → demais por
 * entrega crescente (atrasados naturalmente primeiro; sem entrega no fim)
 * → pedido e ambiente.
 */
export function ordenarFila(abertos) {
  const daFila = (abertos || []).filter((item) => CAMADAS_PROJETADAS.includes(item.camada));
  const emProducao = daFila
    .filter((item) => item.camada === CAMADA.EM_PRODUCAO)
    .sort(
      (a, b) =>
        compararData(a.marcenaria_inicio, b.marcenaria_inicio) ||
        compararData(a.entrega, b.entrega) ||
        desempate(a, b),
    );
  const demais = daFila
    .filter((item) => item.camada !== CAMADA.EM_PRODUCAO)
    .sort((a, b) => compararData(a.entrega, b.entrega) || compararOrigem(a, b) || desempate(a, b));
  return [...emProducao, ...demais];
}

/** Primeiros `n` dias úteis a partir de `hoje` (inclusive, se útil). */
function primeirosDiasUteis(hoje, n, naoUteis) {
  const dias = [];
  if (n < 1) return dias;
  dias.push(diaUtilDeOrdem(hoje, 1, naoUteis));
  while (dias.length < n) dias.push(somarDu(dias[dias.length - 1], 1, naoUteis));
  return dias;
}

function ordemDoDia(x) {
  return Math.max(1, Math.ceil(x - EPSILON));
}

/**
 * Dia útil em que o item de `posicao` termina, no ritmo dado e com a fila
 * esticada por `fator` (1 = central; `1 + erroMin`/`1 + erroMax` = as pontas
 * da faixa de precisão). Mesma régua de `projetarFila`. `posicao` é a carga
 * acumulada até o item (inclusive): na contagem, igual à posição 1..fila;
 * na leitura com pesos, a soma dos pesos (`item.carga`).
 */
export function diaDaPosicao({ posicao, ritmo, fator = 1, hoje, naoUteis }) {
  return diaUtilDeOrdem(hoje, ordemDoDia((posicao / ritmo) * fator), naoUteis);
}

/** Carga acumulada do item numa projeção (cai para a posição nas antigas). */
export function cargaDoItem(item) {
  return Number.isFinite(item?.carga) ? item.carga : item?.posicao;
}

const PESO_UNITARIO = () => 1;

/**
 * `projetarFila({abertos, ritmo, naoUteis, hoje, precisao, peso?})`
 *
 * Retorna `{itens, fila, carga, ritmo, D, Dmin, Dmax, proximaVaga, precisao,
 * hoje, naoUteis, peso}`. Cada item ganha `posicao` (1..fila), `carga`
 * (soma dos pesos até ele, inclusive), `diaProjetado` e `podeAtrasar` (dia
 * projetado depois da entrega). Sem ritmo, `D` e os dias projetados ficam
 * `null` (a tela mostra o motivo do ritmo).
 *
 * `peso(item)` = quanto o item consome de capacidade, na mesma unidade do
 * `ritmo`. Padrão: 1 por ambiente (a régua oficial, que conta ambientes). A
 * segunda leitura (`excecoes.js`) passa o peso das exceções marcadas junto
 * com um ritmo medido nos mesmos pesos; misturar as réguas daria otimismo.
 * O `peso` volta no resultado para quem reprojeta a mesma fila (simulação,
 * cenário do PCP) usar a mesma régua.
 */
export function projetarFila({
  abertos,
  ritmo,
  naoUteis,
  hoje,
  precisao = PRECISAO_REFERENCIA,
  peso = PESO_UNITARIO,
} = {}) {
  const ordenados = ordenarFila(abertos);
  const fila = ordenados.length;
  let acumulada = 0;
  const comCarga = ordenados.map((item, i) => {
    acumulada += peso(item);
    return { ...item, posicao: i + 1, carga: acumulada };
  });
  const base = { fila, carga: acumulada, ritmo: ritmo || null, precisao, hoje, naoUteis, peso };
  if (!ritmo || ritmo <= 0) {
    return {
      ...base,
      itens: comCarga.map((item) => ({ ...item, diaProjetado: null, podeAtrasar: false })),
      D: null,
      Dmin: null,
      Dmax: null,
      proximaVaga: null,
    };
  }

  const D = acumulada / ritmo;
  const Dmin = D * (1 + precisao.erroMin);
  const Dmax = D * (1 + precisao.erroMax);
  const dias = primeirosDiasUteis(hoje, ordemDoDia(Math.max(D, Dmin, Dmax)), naoUteis);
  const itens = comCarga.map((item) => {
    const diaProjetado = dias[ordemDoDia(item.carga / ritmo) - 1];
    return {
      ...item,
      diaProjetado,
      podeAtrasar: Boolean(item.entrega && diaProjetado > item.entrega),
    };
  });
  return {
    ...base,
    itens,
    D,
    Dmin,
    Dmax,
    proximaVaga: {
      de: dias[ordemDoDia(Math.min(Dmin, Dmax)) - 1],
      ate: dias[ordemDoDia(Math.max(Dmin, Dmax)) - 1],
    },
  };
}

export function faixaDeOcupacao(ocupacaoMax) {
  if (ocupacaoMax >= LIMITE_FILA_CHEIA) return FAIXA.FILA_CHEIA;
  if (ocupacaoMax >= LIMITE_APERTADO) return FAIXA.APERTADO;
  return FAIXA.CABE;
}

function limitar(valor, minimo, maximo) {
  return Math.min(maximo, Math.max(minimo, valor));
}

/**
 * Ocupação de um período corrente ou futuro `{inicio, fim}`.
 * `null` para período passado ou projeção sem ritmo. Sem dia útil restante:
 * `{semDiaUtil: true}`. Senão `{restantes, antes, ocupacaoMin, ocupacao,
 * ocupacaoMax, faixa, texto}` (percentuais inteiros; faixa pelo PIOR caso).
 */
export function ocupacaoDoPeriodo(projecao, periodo) {
  const { D, Dmin, Dmax, hoje, naoUteis } = projecao || {};
  if (D === null || D === undefined || periodo.fim < hoje) return null;
  const inicio = maiorIso(periodo.inicio, hoje);
  const restantes = contarDiasUteis(inicio, periodo.fim, naoUteis);
  if (restantes === 0) return { semDiaUtil: true, restantes: 0, texto: "sem dia útil" };
  const antes = contarDiasUteis(hoje, somarDiasCorridos(inicio, -1), naoUteis);
  const percentual = (x) => Math.round((100 * limitar(x - antes, 0, restantes)) / restantes);
  const ocupacaoMin = percentual(Math.min(Dmin, Dmax));
  const ocupacao = percentual(D);
  const ocupacaoMax = percentual(Math.max(Dmin, Dmax));
  return {
    semDiaUtil: false,
    restantes,
    antes,
    ocupacaoMin,
    ocupacao,
    ocupacaoMax,
    faixa: faixaDeOcupacao(ocupacaoMax),
    texto: ocupacaoMin === ocupacaoMax ? `${ocupacaoMax}%` : `${ocupacaoMin}–${ocupacaoMax}%`,
  };
}

function itensDoPeriodo(projecao, periodo) {
  return (projecao?.itens || []).filter(
    (item) => item.diaProjetado && item.diaProjetado >= periodo.inicio && item.diaProjetado <= periodo.fim,
  );
}

/**
 * Clientes que a projeção põe no período, agrupados por pedido + cliente:
 * `[{pedido, cliente, vendaAssinadaId, ambientes, entregas, podeAtrasar, itens}]`,
 * na ordem da fila. A posição é indicativa (o volume é preciso, a ordem não).
 * Venda assinada ainda não tem pedido: agrupa pela venda (`vendaAssinadaId`),
 * para duas vendas do mesmo cliente não virarem uma só.
 */
export function clientesDoPeriodo(projecao, periodo) {
  const grupos = new Map();
  itensDoPeriodo(projecao, periodo).forEach((item) => {
    const chave = `${item.pedido || ""}|${item.cliente || ""}|${item.vendaAssinadaId || ""}`;
    const grupo = grupos.get(chave) || {
      pedido: item.pedido,
      cliente: item.cliente,
      vendaAssinadaId: item.vendaAssinadaId || null,
      itens: [],
    };
    grupo.itens.push(item);
    grupos.set(chave, grupo);
  });
  return [...grupos.values()].map((grupo) => ({
    ...grupo,
    ambientes: grupo.itens.length,
    entregas: [...new Set(grupo.itens.map((item) => item.entrega).filter(Boolean))].sort(),
    podeAtrasar: grupo.itens.some((item) => item.podeAtrasar),
  }));
}

/** Quantos ambientes de cada camada a projeção põe no período. */
export function camadasDoPeriodo(projecao, periodo) {
  const contagem = Object.fromEntries(CAMADAS_DA_FILA.map((camada) => [camada, 0]));
  itensDoPeriodo(projecao, periodo).forEach((item) => {
    contagem[item.camada] = (contagem[item.camada] || 0) + 1;
  });
  return contagem;
}

/** Obra futura (fora da fila) com entrega no período: o "potencial". */
export function obraFuturaDoPeriodo(abertos, periodo) {
  return (abertos || []).filter(
    (item) =>
      item.camada === CAMADA.OBRA_FUTURA &&
      item.entrega &&
      item.entrega >= periodo.inicio &&
      item.entrega <= periodo.fim,
  );
}

/** Abertos da fila com entrega vencida, do mais antigo (entram primeiro). */
export function atrasados(abertos, hoje) {
  return ordenarFila(abertos)
    .filter((item) => item.entrega && item.entrega < hoje)
    .sort((a, b) => compararData(a.entrega, b.entrega) || desempate(a, b));
}
